/**
 * Figure: paints one illustration (described once as regions / ink lines / spots / guides — the core
 * `ArtFigure` model) in a given art style, either at a construction stage or at an exact animation
 * frame (lesson playback):
 *
 *   1 shapes   light-blue construction guides
 *   2 contour  main outline (thick outer silhouette, thinner inner boundaries), paper fill
 *   3 details  eyes, mouth, stripes, whiskers... (tapered ink strokes)
 *   4 erase    guides fade away
 *   5 colour   flat fills + one soft shade crescent + rim highlight / grain / sticker border
 *
 * Outer contour = each silhouette region stroked at 2x width *under* the fills, so only the
 * outside half shows; regions then draw their own thin inner stroke on top.
 */
import { memo, useId, type CSSProperties, type ReactNode } from 'react';
import { appearStage, stageFrame, type Scaffold, type ArtFigure, type ArtFrame, type ArtGuide, type ArtInk, type ArtLayer, type ArtRegion, type ArtSpot, type ArtStage } from '@sketchling/core';
import { mix, taperedStroke } from './geom';
import { STYLES, stylePalette, type ArtStyle, type StyleId } from './tokens';

export type Stage = ArtStage;
export type Region = ArtRegion;
export type Ink = ArtInk;
export type Spot = ArtSpot;
export type Layer = ArtLayer;
export type Guide = ArtGuide;
export type FigureDef = ArtFigure;

export interface FigureProps {
  def: FigureDef;
  styleId: StyleId;
  /** Construction stage to show (ignored when `frame` is given). */
  stage?: Stage;
  /** Exact per-element progress (lesson playback). */
  frame?: ArtFrame;
  /** Animate what appears at `stage` with CSS (draw-on / pop / erase / colour-in). */
  animate?: boolean;
  /** Element refs (`l3`, `g1`…) drawn in the accent colour (the current lesson step). */
  highlight?: readonly string[];
  /** Extra colour roles (world scenes). */
  palette?: Record<string, string>;
  className?: string;
  title?: string;
  style?: CSSProperties;
  /** Hide the ground shadow (e.g. inside a scene). */
  noGround?: boolean;
  /** Clip to the viewBox (scenes). */
  crop?: boolean;
  /** Extra SVG drawn on top, in figure coordinates (e.g. the pencil). */
  overlay?: ReactNode;
  /** Paper rectangle behind the figure (lesson canvas). */
  paper?: string;
  preserveAspectRatio?: string;
  /**
   * Curriculum v2 scaffold fade (flag enableScaffold): trace = guides + a dotted ghost of the final
   * outline (25 %) until it is drawn; guides = classic; mainShapes = only the main guides (+ axes);
   * none = no guides. Default: classic guides.
   */
  scaffold?: Scaffold;
  /** `none` / `mainShapes` scaffold: show the main shapes now ("Need a hint?"). */
  hint?: boolean;
}

/** Rough bounding-box area of path data (numbers read as x y pairs). */
function roughArea(d: string): number {
  const n = (d.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number);
  if (n.length < 4) return 0;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i + 1 < n.length; i += 2) {
    x0 = Math.min(x0, n[i]!);
    x1 = Math.max(x1, n[i]!);
    y0 = Math.min(y0, n[i + 1]!);
    y1 = Math.max(y1, n[i + 1]!);
  }
  return (x1 - x0) * (y1 - y0);
}

/**
 * Guides kept by the `mainShapes` scaffold: every axis + the guides flagged `main`; when none is flagged,
 * the larger half (by area) of the non-axis guides.
 */
export function mainGuideIndexes(def: FigureDef): Set<number> {
  const out = new Set<number>();
  def.guides.forEach((g, i) => (g.axis || g.main) && out.add(i));
  if (def.guides.some((g) => g.main)) return out;
  const shapes = def.guides.map((g, i) => ({ i, a: g.axis ? -1 : roughArea(g.d) })).filter((x) => x.a >= 0);
  shapes.sort((a, b) => b.a - a.a);
  shapes.slice(0, Math.ceil(shapes.length / 2)).forEach((x) => out.add(x.i));
  return out;
}

const delay = (i: number, base = 0, step = 0.09): CSSProperties => ({ animationDelay: `${base + i * step}s` });
const dash = (p: number) => (p < 1 ? { strokeDasharray: '1 1', strokeDashoffset: 1 - p } : null);

export const Figure = memo(function Figure({ def, styleId, stage = 5, frame, animate = false, highlight, palette, className = '', title, style, noGround, crop, overlay, paper, preserveAspectRatio, scaffold, hint }: FigureProps) {
  const S: ArtStyle = STYLES[styleId];
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [w, h] = def.vb;
  const P = palette ? { ...stylePalette(styleId), ...palette } : stylePalette(styleId);
  const col = (r: string) => P[r] ?? P.paper!;
  const fr = frame ?? stageFrame(def, stage);
  const animStage = !frame && animate ? stage : undefined;
  const anim = (atStage: Stage) => animStage === atStage;
  const hl = highlight?.length ? new Set(highlight) : null;
  const accent = S.ui.accent;

  const regions = def.layers.map((l, i) => ({ l, i })).filter((x) => x.l.k === 'region');
  const colourAmt = regions.length ? regions.reduce((a, x) => a + (fr.f[x.i] ?? 0), 0) / regions.length : fr.f.some((v) => v > 0) ? 1 : 0;
  const colour = colourAmt > 0;
  const paperC = col('paper');
  const tint = (fill: string) => (S.lineTint ? mix(S.line, col(fill), S.lineTint) : S.line);
  const fillOf = (r: Region, i: number) => {
    const f = fr.f[i] ?? 0;
    return f >= 1 ? col(r.fill) : f <= 0 ? paperC : mix(paperC, col(r.fill), f);
  };
  const sils = def.layers.map((l, i) => ({ l, i })).filter((x): x is { l: Region; i: number } => x.l.k === 'region' && !!x.l.sil);

  let n = 0; // running index for staggered CSS animation
  const clips: { id: string; d: string }[] = [];
  const clipId = (d?: string) => {
    if (!d) return undefined;
    let c = clips.find((x) => x.d === d);
    if (!c) {
      c = { id: `${uid}c${clips.length}`, d };
      clips.push(c);
    }
    return `url(#${c.id})`;
  };
  const masks: ReactNode[] = [];
  // v2 `clipOut`: inverse clip (hide the element inside the shape of the figure in front).
  const outs: { id: string; d: string }[] = [];
  const outMask = (d?: string) => {
    if (!d) return undefined;
    let c = outs.find((x) => x.d === d);
    if (!c) {
      c = { id: `${uid}o${outs.length}`, d };
      outs.push(c);
    }
    return `url(#${c.id})`;
  };

  const body = def.layers.map((l, i) => {
    const appear = appearStage(l);
    const lp = fr.l[i] ?? 0;
    if (l.k === 'region') {
      const fp = fr.f[i] ?? 0;
      if (lp <= 0 && fp <= 0) return null;
      const k = n++;
      const shadeOn = fp > 0 && l.shade !== false;
      const off = typeof l.shade === 'number' ? l.shade : S.shade.offset;
      const hot = hl?.has(`l${i}`);
      return (
        <g key={i} clipPath={clipId(l.clip)} mask={outMask(l.clipOut)}>
          <path
            d={l.d}
            data-colour={fp > 0 ? col(l.fill) : undefined}
            className={anim(appear) ? 'art-fade' : undefined}
            style={{ fill: fillOf(l, i), transition: 'fill .9s ease', opacity: fp > 0 ? 1 : Math.min(1, lp * 2), ...(anim(appear) ? delay(k) : null) }}
          />
          {shadeOn && (
            <>
              <mask id={`${uid}s${i}`} maskUnits="userSpaceOnUse" x={-20} y={-20} width={w + 40} height={h + 40}>
                <path d={l.d} fill="#fff" />
                <path d={l.d} fill="#000" transform={`translate(${-off * 0.75} ${-off})`} />
              </mask>
              <path d={l.d} fill={mix(col(l.fill), S.shade.color, S.shade.amount)} mask={`url(#${uid}s${i})`} opacity={fp} className={animate ? 'art-fade' : undefined} style={animate ? delay(0, 0.7) : undefined} />
            </>
          )}
          {fp > 0 && l.rim && S.rim.opacity > 0 && (
            <>
              <mask id={`${uid}r${i}`} maskUnits="userSpaceOnUse" x={-20} y={-20} width={w + 40} height={h + 40}>
                <path d={l.d} fill="#fff" />
                <path d={l.d} fill="#000" transform={`translate(${S.rim.offset * 0.8} ${S.rim.offset})`} />
              </mask>
              <path d={l.d} fill={S.rim.color} opacity={S.rim.opacity * fp} mask={`url(#${uid}r${i})`} className={animate ? 'art-fade' : undefined} style={animate ? delay(0, 0.9) : undefined} />
            </>
          )}
          {l.line !== false && lp > 0 && (
            <path
              d={l.d}
              fill="none"
              stroke={hot ? accent : tint(l.fill)}
              strokeWidth={S.inner}
              strokeLinecap="round"
              strokeLinejoin="round"
              pathLength={1}
              className={anim(appear) ? 'art-draw' : undefined}
              style={anim(appear) ? delay(k) : (dash(lp) ?? undefined)}
            />
          )}
        </g>
      );
    }
    if (lp <= 0) return null;
    if (l.k === 'ink') {
      const k = n++;
      const roleCol = l.color && l.color !== 'line' ? col(l.color) : null;
      const c = hl?.has(`l${i}`) ? accent : roleCol && colour ? mix(S.line, roleCol, colourAmt) : S.line;
      const width = S.detail * (l.w ?? 1);
      let mask: string | undefined;
      if (lp < 1) {
        const id = `${uid}k${i}`;
        masks.push(
          <mask key={id} id={id} maskUnits="userSpaceOnUse" x={-20} y={-20} width={w + 40} height={h + 40}>
            <path d={l.d} fill="none" stroke="#fff" strokeWidth={width * 2 + 2} strokeLinecap="round" strokeLinejoin="round" pathLength={1} strokeDasharray="1 1" strokeDashoffset={1 - lp} />
          </mask>,
        );
        mask = `url(#${id})`;
      }
      const shape = (
        <path
          d={taperedStroke(l.d, width, l.taper ?? 'both')}
          data-colour={roleCol && colour ? roleCol : undefined}
          mask={mask}
          className={anim(appear) ? 'art-pop' : undefined}
          style={{ fill: c, transition: 'fill .9s ease', ...(anim(appear) ? delay(k, 0, 0.07) : null) }}
        />
      );
      return (
        <g key={i} clipPath={clipId(l.clip)} mask={outMask(l.clipOut)}>
          {shape}
        </g>
      );
    }
    const k = n++;
    const sp = l as Spot;
    const pop: CSSProperties | null = lp < 1 ? { transformBox: 'fill-box', transformOrigin: 'center', transform: `scale(${0.55 + 0.45 * lp})` } : null;
    return (
      <g key={i} clipPath={clipId(sp.clip)} mask={outMask(sp.clipOut)}>
        <path
          d={sp.d}
          fill={sp.fill === 'shade' ? S.shade.color : sp.fill === 'line' ? S.line : col(sp.fill)}
          data-colour={sp.fill === 'shade' ? undefined : sp.fill === 'line' ? S.line : col(sp.fill)}
          opacity={(sp.opacity ?? 1) * (sp.blush ? S.blush : 1) * (sp.fill === 'shade' ? S.shade.amount * 1.2 : 1) * lp}
          className={anim(appear) ? (appear === 5 ? 'art-fade' : 'art-pop') : undefined}
          style={anim(appear) ? delay(k, appear === 5 ? 0.8 : 0, 0.05) : (pop ?? undefined)}
        />
      </g>
    );
  });

  const silClip = `${uid}sil`;
  const guideFade = anim(4) ? 'art-erase' : undefined;
  const sc = scaffold ?? 'guides';
  const keep = sc === 'mainShapes' || (sc === 'none' && hint) ? mainGuideIndexes(def) : null;
  const showGuide = (i: number) => (sc === 'none' && !hint ? false : keep ? keep.has(i) : true);
  // The hint shows the main shapes at once, whatever the playback frame.
  const guideOpacity = hint && sc !== 'guides' && sc !== 'trace' ? 1 : animStage === 4 ? 1 : 1 - fr.erase;
  // Trace scaffold: dotted ghost of the final outline until each part's outline is drawn (stages 1-2).
  const ghostOf = sils.length ? sils : regions.map((x) => ({ l: x.l as Region, i: x.i }));
  const ghost = sc === 'trace' && !colour && fr.erase <= 0 ? ghostOf.filter(({ i }) => (fr.l[i] ?? 0) < 1) : [];
  return (
    <svg className={`art-figure ${className}`} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={title} style={{ overflow: crop ? 'hidden' : 'visible', ...style }} preserveAspectRatio={preserveAspectRatio}>
      <defs>
        {clips.map((c) => (
          <clipPath key={c.id} id={c.id}>
            <path d={c.d} />
          </clipPath>
        ))}
        {masks}
        {outs.map((o) => (
          <mask key={o.id} id={o.id} maskUnits="userSpaceOnUse" x={-w} y={-h} width={w * 3} height={h * 3}>
            <rect x={-w} y={-h} width={w * 3} height={h * 3} fill="#fff" />
            <path d={o.d} fill="#000" />
          </mask>
        ))}
        <clipPath id={silClip}>
          {sils.map(({ l, i }) => (
            <path key={i} d={l.d} />
          ))}
        </clipPath>
        {S.grain > 0 && colour && (
          <filter id={`${uid}g`} x="0" y="0" width="100%" height="100%" filterUnits="userSpaceOnUse">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="7" stitchTiles="stitch" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.28  0 0 0 0 0.16  0 0 0 0 0.1  0 0 0 -2.2 1.35" />
          </filter>
        )}
        {S.grain > 0 && colour && (
          <filter id={`${uid}m`} x="0" y="0" width="100%" height="100%" filterUnits="userSpaceOnUse">
            <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="3" seed="3" />
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 0.97  0 0 0 0 0.9  0 0 0 2.4 -1.1" />
          </filter>
        )}
        {S.sticker.width > 0 && colour && (
          <filter id={`${uid}d`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="0" floodColor={S.sticker.shadow} floodOpacity="1" />
          </filter>
        )}
      </defs>

      {paper && <rect x={-w} y={-h} width={w * 3} height={h * 3} fill={paper} />}

      {colour && def.ground && !noGround && (
        <ellipse cx={def.ground[0]} cy={def.ground[1]} rx={def.ground[2]} ry={def.ground[3]} fill={col('ground')} opacity={colourAmt} className={animate ? 'art-fade' : undefined} />
      )}

      {colour && S.sticker.width > 0 && (
        <g filter={`url(#${uid}d)`} opacity={colourAmt} className={animate ? 'art-fade' : undefined}>
          {sils.map(({ l, i }) => (
            <path key={i} d={l.d} fill={col('white')} stroke={col('white')} strokeWidth={2 * (S.outer + S.sticker.width)} strokeLinejoin="round" mask={outMask(l.clipOut)} />
          ))}
        </g>
      )}

      <g>
        {sils.map(({ l, i }, k) => {
          const lp = fr.l[i] ?? 0;
          if (lp <= 0 && (fr.f[i] ?? 0) <= 0) return null;
          return (
            <path
              key={i}
              d={l.d}
              fill={fillOf(l, i)}
              stroke={hl?.has(`l${i}`) ? accent : tint(l.fill)}
              strokeWidth={2 * S.outer}
              strokeLinejoin="round"
              strokeLinecap="round"
              pathLength={1}
              mask={outMask(l.clipOut)}
              className={anim(2) ? 'art-draw' : undefined}
              style={{ transition: 'fill .9s ease', fillOpacity: (fr.f[i] ?? 0) > 0 ? 1 : Math.min(1, lp * 2), ...(anim(2) ? delay(k, 0, 0.12) : dash(lp)) }}
            />
          );
        })}
      </g>

      {body}

      {colour && S.grain > 0 && (
        <g clipPath={`url(#${silClip})`} style={{ mixBlendMode: 'multiply', pointerEvents: 'none' }} opacity={colourAmt} className={animate ? 'art-fade' : undefined}>
          <rect x={0} y={0} width={w} height={h} filter={`url(#${uid}g)`} opacity={S.grain} />
          <rect x={0} y={0} width={w} height={h} filter={`url(#${uid}m)`} opacity={0.5} style={{ mixBlendMode: 'screen' }} />
        </g>
      )}

      {ghost.length > 0 && (
        <g className="art-ghost" opacity={0.25} pointerEvents="none" data-testid="scaffold-ghost">
          {ghost.map(({ l, i }) => (
            <path key={i} d={l.d} fill="none" stroke={S.line} strokeWidth={S.inner} strokeDasharray="3 3" strokeLinecap="round" strokeLinejoin="round" mask={outMask(l.clipOut)} />
          ))}
        </g>
      )}

      {guideOpacity > 0 && def.guides.length > 0 && (
        <g className={`${guideFade ?? ''}${hint ? ' art-hint' : ''}`} opacity={guideOpacity} pointerEvents="none" data-scaffold={sc}>
          {def.guides.map((g, i) => {
            if (!showGuide(i)) return null;
            const gp = hint && sc !== 'guides' && sc !== 'trace' ? 1 : (fr.g[i] ?? 0);
            if (gp <= 0) return null;
            return (
              <path
                key={i}
                d={g.d}
                fill="none"
                stroke={S.guide.color}
                strokeWidth={S.guide.width * (hl?.has(`g${i}`) ? 1.5 : 1)}
                strokeDasharray={g.axis ? '5 5' : undefined}
                strokeLinecap="round"
                opacity={g.axis ? 0.95 * gp : 0.95}
                pathLength={g.axis ? undefined : 1}
                className={anim(1) && !g.axis ? 'art-draw' : anim(1) ? 'art-fade' : undefined}
                style={anim(1) ? delay(i, 0, 0.18) : g.axis ? undefined : (dash(gp) ?? undefined)}
              />
            );
          })}
        </g>
      )}
      {overlay}
    </svg>
  );
});

export function useStyle(id: StyleId) {
  return STYLES[id];
}
